import { useEffect, useState } from 'react'
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'
import type { SeriesDoc } from '@racedash/shared'
import { db } from '../lib/firebase'

export interface Series extends SeriesDoc {
  id: string
}

/**
 * The championship catalogue.
 *
 * World-readable, and deliberately so: it is the mapping between what a series
 * is called and what each vendor's stream reports it as, which every viewer
 * needs to work out which sessions belong to whom — including a share-link
 * holder who has no organisation at all.
 *
 * Small by nature. A handful of championships, read whole.
 */
export function useSeries() {
  const [series, setSeries] = useState<Series[] | null>(null)

  useEffect(() => {
    return onSnapshot(
      query(collection(db, 'series'), orderBy('name')),
      (snap) => setSeries(snap.docs.map((d) => ({ id: d.id, ...(d.data() as SeriesDoc) }))),
      () => setSeries([]),
    )
  }, [])

  return series
}

/**
 * What a stream calls this series, for matching against a bucket.
 *
 * The catalogue's own strings, exactly as entered, so comparison stays exact
 * rather than fuzzy — the whole point of keeping a registry is not having to
 * guess. An entry pointing at a series that has since been deleted resolves to
 * nothing, which claims nothing, which is the safe reading.
 */
export function vendorNamesFor(
  series: readonly Series[] | null,
  seriesId: string | undefined,
  vendor: 'ris' | 'tsl' = 'ris',
): string[] | undefined {
  if (!seriesId || !series) return undefined
  const found = series.find((s) => s.id === seriesId)
  const names = found?.vendorNames?.[vendor]
  return names && names.length > 0 ? names : undefined
}
